import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';

export interface ReingestStack {
  /** `cam1`, `pgm`, … */
  stackId: string;
  uuid: string;
  /** TM config name, e.g. `KIRBY-CAR@VGK-2026-06-07-cam1`. */
  name: string;
  /**
   * Where video is pushed. Only set once TM reported the stack running, so its
   * presence is itself proof the stack came up.
   */
  ingestUrl: string | null;
}

/**
 * What TiledMedia last reported about one of this game's configs.
 *
 * The counterpart to {@link ReingestStack}, which records what the launch asked
 * for. Joined on `feed`, and duplicated per feed for the usual reason: TM makes
 * a config per launch rather than upserting by name. Use `resolveTmStack` to
 * pick the one that answers.
 */
export interface ReingestTmStack {
  /** `cam1`, `pgm`, … — the join key. */
  feed: string;
  configUuid: string;
  stackUuid: string | null;
  /** TM's own state, or `unknown` on a row no poll has touched. */
  state: string;
  deploymentFailure: string | null;
  /** Bucket and `${EVENT}`-resolved folder TM writes this feed's HLS into. */
  egressBucket: string | null;
  egressPrefix: string | null;
}

export interface ReingestStatus {
  /** queued | tm_launching | tm_ready | ingesting | stopping | stopped | failed | not_started */
  status: string;
  reingestStartedAt?: string;
  reingestCompletedAt?: string;
  stackCount?: number;
  stacks?: ReingestStack[];
  tmStacks?: ReingestTmStack[];
  tmSetId?: string | null;
  /** The gstreamer instance pushing the recorded chunks into TM. */
  instanceId?: string | null;
  /** Its public IP. Null until the box launches, and cleared when it stops. */
  instanceIp?: string | null;
  /**
   * The S3 folder the ingester reads this replay out of, recorded at launch.
   * Null until the ingester starts — the stacks come up before it.
   */
  sourceUri?: string | null;
  /** Why a run stopped short, e.g. the ingester not being configured yet. */
  note?: string;
}

/** Statuses where a replay is running or on its way there. */
const ACTIVE_STATUSES = [
  'queued',
  'tm_launching',
  'tm_ready',
  'ingesting',
  'stopping',
];

export const isReplayActive = (status?: string) =>
  !!status && ACTIVE_STATUSES.includes(status);

/**
 * Polls a game's replay status. Keyed per game so several rows can share one
 * request, and polled because starting a replay takes minutes — TM has to
 * deploy before the gstreamer box can be launched.
 */
export const useReingestStatusQuery = (gameId: number) =>
  useQuery({
    queryKey: ['simuliveStatus', gameId],
    queryFn: async () => {
      const { data } = await get<ReingestStatus>(
        `/v1.0/games/${gameId}/reingest/status`,
      );
      return data;
    },
    refetchInterval: 5000,
  });
